<script setup lang="ts">
import moment from 'moment-jalaali'
import { useField, useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import * as yup from 'yup'
import {
  createInternationalAccountAction,
  createInternationalPaypalAccountAction,
  createInternationalRevolutAccountAction,
  createInternationalWiseAccountAction,
  createIranianAccountAction,
  createIranianSrcAccountAction,
} from '@/api/functions/bank-account/create-bank-account.action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import ErrorIcon from '/svg/error-icon.svg'

const props = defineProps({
  modalUpdate: Boolean,
  userId: { type: Number },
  UserAccountType: { type: String },
  UserInternationalType: { type: String },
})

const emit = defineEmits(['update:modalUpdate', 'confirm', 'cancel'])

const toast = useToast()

// Reactive data
const accountType = ref('')
const internationalType = ref('')
const loading = ref(false)

// Options
const poundOptions = [
  { label: 'بله', value: true },
  { label: 'خیر', value: false },
]

const dayOptions = computed(() =>
  Array.from({ length: 31 }, (_, i) => ({
    label: (i + 1).toString(),
    value: (i + 1).toString().padStart(2, '0'),
  }))
)

const monthOptions = computed(() =>
  Array.from({ length: 12 }, (_, i) => ({
    label: (i + 1).toString(),
    value: (i + 1).toString().padStart(2, '0'),
  }))
)

const yearOptions = computed(() =>
  Array.from({ length: 104 }, (_, i) => ({
    label: (1403 - i).toString(),
    value: (1403 - i).toString(),
  }))
)

// Validation Schema
const createValidationSchema = () => {
  const baseInternationalSchema = {
    bankName: yup
      .string()
      .required('نام بانک الزامی است')
      .min(2)
      .max(100)
      .matches(/^[a-zA-Z ]*$/, 'نام بانک باید فقط حروف انگلیسی باشد'),
    ownerName: yup
      .string()
      .required('نام دارنده حساب الزامی است')
      .min(3)
      .max(100)
      .matches(/^[a-zA-Z ]*$/, 'نام دارنده حساب باید فقط حروف انگلیسی باشد'),
    country: yup.string().required('کشور الزامی است'),
    paypalEmail: yup.string().nullable(),
    paypalId: yup.string().nullable(),
    iban: yup.string().nullable(),
    bic: yup.string().nullable(),
    isPound: yup.boolean().nullable(),
    sortCode: yup.string().nullable(),
    accountNumber: yup.string().nullable(),
    revolutTag: yup.string().nullable(),
  }

  const baseDomesticSchema = {
    sheba: yup
      .string()
      .required('شماره شبا الزامی است')
      .matches(/^[0-9]{24}$/, 'شماره شبا باید ۲۴ رقم باشد'),
    cardNumber: yup
      .string()
      .required('شماره کارت الزامی است')
      .length(16)
      .matches(/^[0-9]+$/, 'شماره کارت باید فقط عدد باشد'),
    bankName: yup.string().required('نام بانک الزامی است').min(2).max(100),
    ownerName: yup.string().required('نام دارنده حساب الزامی است').min(3).max(100),
    nationalCode: yup
      .string()
      .required('کد ملی الزامی است')
      .length(10)
      .matches(/^[0-9]+$/, 'کد ملی باید فقط عدد باشد'),
  }

  if (accountType.value === 'بین المللی') {
    if (internationalType.value === 'بین بانکی') {
      return yup.object({
        ...baseInternationalSchema,
        iban: yup
          .string()
          .required('شماره IBAN الزامی است')
          .transform((v) => v.replace(/\s|-/g, '').toUpperCase())
          .matches(/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/),
        bic: yup.string().required('شماره BIC الزامی است').min(8).max(11),
        sortCode: yup.string().when('isPound', {
          is: true,
          then: (schema) => schema.required('شماره Sort Code برای حساب پوند الزامی است'),
        }),
        accountNumber: yup.string().when('isPound', {
          is: true,
          then: (schema) => schema.required('شماره Account Number برای حساب پوند الزامی است'),
        }),
      })
    } else if (internationalType.value === 'رولوت') {
      return yup.object({
        ...baseInternationalSchema,
        iban: yup
          .string()
          .required('شماره IBAN الزامی است')
          .transform((v) => v.replace(/\s|-/g, '').toUpperCase())
          .matches(/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/),
        bic: yup.string().required('شماره BIC الزامی است').min(8).max(11),
        revolutTag: yup.string().required('Revolut Tag الزامی است').min(2).max(50),
      })
    } else if (internationalType.value === 'پی پال') {
      return yup.object({
        ...baseInternationalSchema,
        paypalEmail: yup.string().required('ایمیل Paypal الزامی است').email(),
        paypalId: yup.string().nullable().max(50),
      })
    } else if (internationalType.value === 'وایز') {
      return yup.object({
        ...baseInternationalSchema,
        iban: yup
          .string()
          .required('شماره IBAN الزامی است')
          .transform((v) => v.replace(/\s|-/g, '').toUpperCase())
          .matches(/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/),
        bic: yup.string().required('شماره BIC الزامی است').min(8).max(11),
      })
    }
  } else if (accountType.value === 'مقصد ریالی' || accountType.value === 'مبداء ریالی') {
    return yup.object({
      ...baseDomesticSchema,
      birthDate: yup.object({
        day: yup.string().required('روز تولد الزامی است'),
        month: yup.string().required('ماه تولد الزامی است'),
        year: yup.string().required('سال تولد الزامی است'),
      }),
    })
  }

  return yup.object({})
}

const validationSchema = computed(() => createValidationSchema())

const { resetForm, errors } = useForm({
  validationSchema,
  initialValues: {
    paypalEmail: '',
    paypalId: '',
    iban: '',
    bic: '',
    bankName: '',
    ownerName: '',
    country: '',
    isPound: null,
    sortCode: '',
    accountNumber: '',
    revolutTag: '',
    sheba: '',
    cardNumber: '',
    nationalCode: '',
    birthDate: { day: '', month: '', year: '' },
  },
})

// Fields
const { value: paypalEmail } = useField('paypalEmail')
const { value: paypalId } = useField('paypalId')
const { value: iban } = useField('iban')
const { value: bic } = useField('bic')
const { value: bankName } = useField('bankName')
const { value: ownerName } = useField('ownerName')
const { value: country } = useField('country')
const { value: isPound } = useField('isPound')
const { value: sortCode } = useField('sortCode')
const { value: accountNumber } = useField('accountNumber')
const { value: revolutTag } = useField('revolutTag')

const { value: sheba } = useField('sheba')
const { value: cardNumber } = useField('cardNumber')
const { value: nationalCode } = useField('nationalCode')
const { value: birthDate } = useField<{ day: string; month: string; year: string }>('birthDate')

// Computed fields
const ibanField = computed({
  get: () => (internationalType.value === 'پی پال' ? paypalEmail.value : iban.value),
  set: (v) => {
    if (internationalType.value === 'پی پال') paypalEmail.value = v
    else iban.value = v
  },
})
const bicField = computed({
  get: () => (internationalType.value === 'پی پال' ? paypalId.value : bic.value),
  set: (v) => {
    if (internationalType.value === 'پی پال') paypalId.value = v
    else bic.value = v
  },
})

// ----------------------
// Mutations
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { mutate: createIranianAccount } = useMutationApi<unknown, { userId: string; payload: any }>({
  mutationKey: ['createIranianAccount'],
  mutationFn: ({ userId, payload }) => createIranianAccountAction({ userId: String(userId), ...payload }),
  onSuccess: () => {
    loading.value = false
    toast.success('حساب مقصد ریالی با موفقیت ایجاد شد')
    emit('update:modalUpdate', false)
    emit('confirm')
  },
  onError: (error: unknown) => {
    loading.value = false
    toast.error(getErrorMessage(error))
  },
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { mutate: createIranianSrcAccount } = useMutationApi<unknown, { userId: string; payload: any }>({
  mutationKey: ['createIranianSrcAccount'],
  mutationFn: ({ userId, payload }) => createIranianSrcAccountAction({ userId: String(userId), ...payload }),
  onSuccess: () => {
    loading.value = false
    toast.success('حساب مبداء ریالی با موفقیت ایجاد شد')
    emit('update:modalUpdate', false)
    emit('confirm')
  },
  onError: (error: unknown) => {
    loading.value = false
    toast.error(getErrorMessage(error))
  },
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { mutate: createInternationalAccount } = useMutationApi<unknown, { userId: string; payload: any }>({
  mutationKey: ['createInternationalAccount'],
  mutationFn: ({ userId, payload }) => createInternationalAccountAction({ userId: String(userId), ...payload }),
  onSuccess: () => {
    loading.value = false
    toast.success('حساب بین‌المللی با موفقیت ایجاد شد')
    emit('update:modalUpdate', false)
    emit('confirm')
  },
  onError: (error: unknown) => {
    loading.value = false
    toast.error(getErrorMessage(error))
  },
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { mutate: createInternationalRevolutAccount } = useMutationApi<unknown, { userId: string; payload: any }>({
  mutationKey: ['createInternationalRevolutAccount'],
  mutationFn: ({ userId, payload }) => createInternationalRevolutAccountAction({ userId: String(userId), ...payload }),
  onSuccess: () => {
    loading.value = false
    toast.success('حساب رولوت با موفقیت ایجاد شد')
    emit('update:modalUpdate', false)
    emit('confirm')
  },
  onError: (error: unknown) => {
    loading.value = false
    toast.error(getErrorMessage(error))
  },
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { mutate: createInternationalPaypalAccount } = useMutationApi<unknown, { userId: string; payload: any }>({
  mutationKey: ['createInternationalPaypalAccount'],
  mutationFn: ({ userId, payload }) => createInternationalPaypalAccountAction({ userId: String(userId), ...payload }),
  onSuccess: () => {
    loading.value = false
    toast.success('حساب پی‌پال با موفقیت ایجاد شد')
    emit('update:modalUpdate', false)
    emit('confirm')
  },
  onError: (error: unknown) => {
    loading.value = false
    toast.error(getErrorMessage(error))
  },
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const { mutate: createInternationalWiseAccount } = useMutationApi<unknown, { userId: string; payload: any }>({
  mutationKey: ['createInternationalWiseAccount'],
  mutationFn: ({ userId, payload }) => createInternationalWiseAccountAction({ userId: String(userId), ...payload }),
  onSuccess: () => {
    loading.value = false
    toast.success('حساب وایز با موفقیت ایجاد شد')
    emit('update:modalUpdate', false)
    emit('confirm')
  },
  onError: (error: unknown) => {
    loading.value = false
    toast.error(getErrorMessage(error))
  },
})

// ----------------------
// Submit
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const submitForm = (values: any) => {
  loading.value = true
  try {
    if (accountType.value === 'بین المللی') {
      if (internationalType.value === 'بین بانکی') {
        createInternationalAccount({
          userId: String(props.userId),
          payload: {
            iban_number: values.iban,
            bic_number: values.bic,
            bank_name: values.bankName,
            owner_full_name: values.ownerName,
            country_id: values.country,
            ...(values.isPound
              ? {
                  bank_account_sort_code: values.sortCode,
                  bank_account_number: values.accountNumber,
                  is_pound: true,
                  type: 'POUND',
                }
              : {}),
          },
        })
      } else if (internationalType.value === 'رولوت') {
        createInternationalRevolutAccount({
          userId: String(props.userId),
          payload: {
            iban_number: values.iban,
            bic_number: values.bic,
            bank_name: values.bankName,
            owner_full_name: values.ownerName,
            country_id: values.country,
            revolut_tag: values.revolutTag,
          },
        })
      } else if (internationalType.value === 'پی پال') {
        createInternationalPaypalAccount({
          userId: String(props.userId),
          payload: {
            paypal_email: values.paypalEmail,
            paypal_id: values.paypalId || undefined,
            bank_name: values.bankName,
            owner_full_name: values.ownerName,
            country_id: values.country,
          },
        })
      } else if (internationalType.value === 'وایز') {
        createInternationalWiseAccount({
          userId: String(props.userId),
          payload: {
            iban_number: values.iban,
            bic_number: values.bic,
            bank_name: values.bankName,
            owner_full_name: values.ownerName,
            country_id: values.country,
          },
        })
      }
    } else if (accountType.value === 'مقصد ریالی' || accountType.value === 'مبداء ریالی') {
      const shebaWithIR = `IR${values.sheba}`
      const jalaaliDate = `${values.birthDate.year}/${values.birthDate.month}/${values.birthDate.day}`
      const gregorianDate = moment(jalaaliDate, 'jYYYY/jMM/jDD').format('YYYY-MM-DD')

      if (accountType.value === 'مقصد ریالی') {
        createIranianAccount({
          userId: String(props.userId),
          payload: {
            sheba_number: shebaWithIR,
            card_number: values.cardNumber,
            bank_name: values.bankName,
            owner_full_name: values.ownerName,
            national_code: values.nationalCode,
            birth_date: gregorianDate,
          },
        })
      } else {
        createIranianSrcAccount({
          userId: String(props.userId),
          payload: {
            sheba_number: shebaWithIR,
            card_number: values.cardNumber,
            bank_name: values.bankName,
            owner_full_name: values.ownerName,
            national_code: values.nationalCode,
            birth_date: gregorianDate,
          },
        })
      }
    }
  } catch (error) {
    loading.value = false
    toast.error(getErrorMessage(error))
  }
}

// ----------------------
const filterEnglishCharacters = (event: Event) => {
  const input = event.target as HTMLInputElement
  const englishOnlyRegex = /^[a-zA-Z ]*$/
  if (!englishOnlyRegex.test(input.value)) {
    input.value = input.value.replace(/[^a-zA-Z ]/g, '')
  }
}

const closeModal = () => {
  emit('update:modalUpdate', false)
  emit('cancel')
}

watch(
  () => props.userId,
  (newId) => {
    if (!newId) return
    resetForm()
    accountType.value = props.UserAccountType ?? ''
    internationalType.value = props.UserInternationalType ?? ''
  },
  { immediate: true }
)

watch(
  [() => props.UserAccountType, () => props.UserInternationalType],
  ([newAccountType, newInternationalType]) => {
    if (!newAccountType) return
    accountType.value = newAccountType
    internationalType.value = newInternationalType || ''
    resetForm()
  },
  { immediate: true }
)

watch(
  () => props.modalUpdate,
  (isOpen) => {
    if (!isOpen) {
      accountType.value = ''
      internationalType.value = ''
      resetForm()
    }
  }
)
</script>
<template>
  <Modal :model-value="props.modalUpdate" @update:model-value="$emit('update:modalUpdate', $event)" width="650px"
    background="#FFF" :closable="true" title="ایجاد حساب" closeDirection="left" class="update-request">
    <form @submit.prevent="submitForm" class="flex flex-col min-h-[530px]">

      <div class="flex-1 overflow-y-auto px-4 py-6 pb-9">
        <div>
          <!-- Info Banner -->
          <div v-if="accountType && (accountType !== 'بین المللی' || internationalType)" class="mb-10">
            <div class="bg-[#faf2db] rounded-xl p-2.5 text-black text-sm font-normal">
              <p v-if="accountType === 'بین المللی'">
                حساب بانکی که خریدار قصد دارد مبلغ ارزی به آن واریز شود برای مثال (حساب بانکی، پی پال،
                رولوت، وایز و ...)
              </p>
              <p v-if="accountType === 'مقصد ریالی'">
                حساب بانکی که فروشنده قصد دارد تسویه ریالی توسط پی جیب، با آن حساب انجام شود.
              </p>
              <p v-if="accountType === 'مبداء ریالی'">
                حساب بانکی که خریدار از طریق این حساب قصد واریز مبلغ ریالی به پی جیب را دارد.
              </p>
            </div>
          </div>

          <!-- Form -->
          <div v-if="accountType && (accountType !== 'بین المللی' || internationalType)" class="space-y-6">
            <!-- International Account Fields -->
            <div v-if="accountType === 'بین المللی'">
              <!-- PayPal Fields -->
              <div v-if="internationalType === 'پی پال'" class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
                <div>
                  <Input v-model="ibanField" name="ibanField" label="ایمیل Paypal را وارد کنید" type="email"
                    placeholder="example@paypal.com" />
                  <div v-if="errors.paypalEmail"
                    class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                    <img :src="ErrorIcon" width="15" alt="error-icon" />
                    <span>{{ errors.paypalEmail }}</span>
                  </div>
                </div>
                <div>
                  <Input v-model="bicField" name="bicField" :isTag="true" label="Paypal Id را وارد کنید" type="text"
                    placeholder="PID123456789" />
                  <div v-if="errors.paypalId"
                    class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                    <img :src="ErrorIcon" width="15" alt="error-icon" />
                    <span>{{ errors.paypalId }}</span>
                  </div>
                </div>
              </div>

              <!-- IBAN & BIC Fields -->
              <div v-if="internationalType === 'بین بانکی' || internationalType === 'رولوت' || internationalType === 'وایز'"
                class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
                <div>
                  <Input v-model="ibanField" name="ibanField" label="شماره IBAN را وارد کنید" type="text"
                    placeholder="IE84REVO99036020197146" />
                  <div v-if="errors.iban" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                    <img :src="ErrorIcon" width="15" alt="error-icon" />
                    <span>{{ errors.iban }}</span>
                  </div>
                </div>
                <div>
                  <Input v-model="bicField" name="bicField" label="شماره BIC CODE را وارد کنید" type="text"
                    placeholder="REVOIE2D" />
                  <div v-if="errors.bic" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                    <img :src="ErrorIcon" width="15" alt="error-icon" />
                    <span>{{ errors.bic }}</span>
                  </div>
                </div>
              </div>

              <!-- Common International Fields -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
                <div>
                  <Input v-model="bankName" name="bankName" @input="filterEnglishCharacters"
                    label="نام بانک را به انگلیسی وارد کنید" type="text" placeholder="Bank Name" />

                </div>
                <div>
                  <Input v-model="ownerName" name="ownerName" @input="filterEnglishCharacters"
                    label="نام دارنده حساب را به انگلیسی وارد کنید" type="text" placeholder="Full Name" />
                </div>
              </div>

              <div class="flex flex-col lg:grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <CountrySelect v-model="country" name="country" label="کشور مقصد حساب ارزی خود را انتخاب کنید" />
                  <div v-if="errors.country" class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                    <img :src="ErrorIcon" width="15" alt="error-icon" />
                    <span>{{ errors.country }}</span>
                  </div>
                </div>

                <div v-if="internationalType === 'بین بانکی'" class="pt-3">
                  <Radio v-model="isPound" name="isPound" label="آیا حساب شما پوند است؟" :options="poundOptions" />
                </div>

                <!-- Bank Transfer Specific Fields -->
                <div v-if="isPound === true && internationalType === 'بین بانکی'"
                  class="col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <Input v-model="accountNumber" name="accountNumber" label="مقدار Account Number را وارد کنید"
                      type="text" placeholder="12345678" />
                  </div>
                  <div>
                    <Input v-model="sortCode" name="sortCode" label="مقدار Sort Code را وارد کنید" type="text"
                      placeholder="123456" />

                  </div>
                </div>
                <!-- Revolut Specific Field -->
                <div v-if="internationalType === 'رولوت'">
                  <Input v-model="revolutTag" :isTag="true" name="revolutTag" label="Revolut Tag را وارد کنید"
                    type="text" placeholder="@username" />
                </div>
              </div>

            </div>

            <!-- Domestic Account Fields -->
            <div v-if="accountType === 'مقصد ریالی' || accountType === 'مبداء ریالی'">
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
                <div>
                  <Input v-model="sheba" name="sheba" :isSheba="true" label="شماره شبا را وارد کنید" type="text"
                    placeholder="060120000000009886055009" />
                </div>
                <div>
                  <Input v-model="cardNumber" name="cardNumber" label="شماره کارت را وارد کنید" type="text"
                    placeholder="6104338650180389" />
                </div>
              </div>

              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 space-y-6">
                <div>
                  <Input v-model="bankName" name="bankName" label="نام بانک را وارد کنید" type="text"
                    placeholder="ملی تست" />

                </div>
                <div>
                  <Input v-model="ownerName" name="ownerName" label="نام دارنده حساب را وارد کنید" type="text"
                    />
                </div>
              </div>

              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <Input v-model="nationalCode" name="nationalCode" label="کد ملی دارنده حساب را وارد کنید" type="text"
                    />
                </div>
                <div>
                  <div class="grid lg:grid-cols-3 gap-4">
                    <Select v-model="birthDate.day" name="birthDate.day" :options="dayOptions" label="روز تولد" />
                    <Select v-model="birthDate.month" name="birthDate.month" :options="monthOptions" label="ماه تولد" />
                    <Select v-model="birthDate.year" name="birthDate.year" :options="yearOptions" label="سال تولد" />
                  </div>
                  <div v-if="errors.birthDate"
                    class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] -mt-4">
                    <img :src="ErrorIcon" width="15" alt="error-icon" />
                    <span>تاریخ تولد الزامی است</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
        <Button type="submit" label="تایید" :isAdmin="true" class="w-9/12 h-12 rounded-xl" />
        <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
          class="w-4/12 h-12 rounded-xl border-2" />
      </div>
    </form>
  </Modal>
</template>
